import { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, 
  FolderKanban, 
  LineChart, 
  Ticket, 
  Receipt, 
  FileBox,
  Users,
  ScrollText,
  Bot,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  Building2,
  CreditCard,
  HandCoins,
  FileText,
  Share2,
  Shield,
  Mail,
  Inbox,
  Link2,
  BookOpen,
  Settings2,
  BarChart3,
  ClipboardList,
  Search,
  Loader2,
  MessageSquare,
  FileSignature,
  FolderGit2,
  CalendarDays,
  Megaphone,
  QrCode,
  Store,
  UserPlus,
  BookUser,
  Tv,
  Images,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import PushNotificationToggle from "@/components/PushNotificationToggle";
import WorkspaceSelector from "@/components/WorkspaceSelector";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import axios from "axios";

const LOGO_ICON = "/logo-glotech-icon.png";

// Navigation items differ based on role and client_type
const ORG_ROLES = ["org_admin", "event_manager", "receptionist", "viewer"];
const ROLE_LABELS = {
  admin: "Administrateur",
  org_admin: "Organisateur",
  event_manager: "Gestionnaire",
  receptionist: "Agent d'accueil",
  viewer: "Observateur",
  client: "Client",
};

const getNavItems = (isAdmin, isSuperAdmin, clientType, userRole, activeWorkspace) => {
  // Event Center roles (org members) — always event_center
  if (userRole === "org_admin") {
    return [
      { icon: LayoutDashboard, label: "Mon Espace", path: "/app", exact: true },
      { icon: CalendarDays, label: "Mes Evenements", path: "/app/evenements", highlight: true },
      { icon: QrCode, label: "Scanner QR", path: "/app/scanner", highlight: true },
      { icon: Images, label: "Galeries", path: "/app/galeries" },
      { icon: Store, label: "Prestataires", path: "/app/marketplace" },
      { icon: UserPlus, label: "Mon Equipe", path: "/app/equipe" },
      { icon: Building2, label: "Profil Organisation", path: "/app/profil-organisation" },
      { icon: MessageSquare, label: "Messagerie", path: "/app/messagerie" },
      { icon: Ticket, label: "Mes Tickets", path: "/app/tickets" },
      { icon: FolderGit2, label: "Mes Fichiers", path: "/app/fichiers" },
      { icon: User, label: "Mon Profil", path: "/app/profil" },
    ];
  }

  if (userRole === "event_manager") {
    return [
      { icon: LayoutDashboard, label: "Mon Espace", path: "/app", exact: true },
      { icon: CalendarDays, label: "Mes Evenements", path: "/app/evenements", highlight: true },
      { icon: QrCode, label: "Scanner QR", path: "/app/scanner", highlight: true },
      { icon: Images, label: "Galeries", path: "/app/galeries" },
      { icon: User, label: "Mon Profil", path: "/app/profil" },
    ];
  }

  if (userRole === "receptionist") {
    return [
      { icon: LayoutDashboard, label: "Mon Espace", path: "/app", exact: true },
      { icon: QrCode, label: "Scanner QR", path: "/app/scanner", highlight: true },
      { icon: CalendarDays, label: "Evenements", path: "/app/evenements" },
      { icon: User, label: "Mon Profil", path: "/app/profil" },
    ];
  }

  if (userRole === "viewer") {
    return [
      { icon: LayoutDashboard, label: "Mon Espace", path: "/app", exact: true },
      { icon: CalendarDays, label: "Evenements", path: "/app/evenements" },
      { icon: User, label: "Mon Profil", path: "/app/profil" },
    ];
  }

  // Admin users: navigation depends on active workspace
  if (isAdmin) {
    // Event Center workspace for admin
    if (activeWorkspace === "event_center") {
      const items = [
        { icon: LayoutDashboard, label: "Event Center", path: "/app", exact: true },
      ];
      if (isSuperAdmin) {
        items.push({ icon: BookUser, label: "Referentiel Contacts", path: "/app/contacts-referentiel", highlight: true });
        items.push({ icon: Tv, label: "Vitrine Medias", path: "/app/vitrine-medias", highlight: true });
      }
      items.push(
        { icon: CalendarDays, label: "Organisations", path: "/app/event-center", highlight: true },
        { icon: Images, label: "Galeries", path: "/app/galeries" },
        { icon: Store, label: "Gestion Prestataires", path: "/app/admin-prestataires" },
        { icon: Megaphone, label: "Regie publicitaire", path: "/app/regie-publicitaire" },
        { icon: MessageSquare, label: "Messagerie", path: "/app/messagerie" },
        { icon: Ticket, label: "Tickets", path: "/app/tickets" },
      );
      return items;
    }

    // Default: Web workspace for admin
    const items = [
      { icon: LayoutDashboard, label: "KPI Dashboard", path: "/app", exact: true },
    ];
    if (isSuperAdmin) {
      items.push({ icon: BarChart3, label: "Super Admin", path: "/app/super-admin", highlight: true });
    }
    items.push(
      { icon: Users, label: "Clients", path: "/app/clients" },
      { icon: Users, label: "Leads", path: "/app/leads" },
      { icon: ClipboardList, label: "Questionnaires", path: "/app/questionnaires" },
      { icon: Bot, label: "Assistant IA", path: "/app/assistant" },
      { icon: FolderKanban, label: "Projets", path: "/app/projets" },
      { icon: LineChart, label: "SEO & Rapports", path: "/app/seo" },
      { icon: Ticket, label: "Tickets", path: "/app/tickets" },
      { icon: MessageSquare, label: "Messagerie", path: "/app/messagerie" },
      { icon: FileBox, label: "Fichiers", path: "/app/fichiers" },
      { icon: ScrollText, label: "Documents", path: "/app/documents" },
      { icon: FileSignature, label: "Contrats", path: "/app/contrats" },
      { icon: Receipt, label: "Factures", path: "/app/factures" },
      { icon: CreditCard, label: "Plans & Tarifs", path: "/app/plans" },
      { icon: HandCoins, label: "Affilies", path: "/app/affilies" },
      { icon: FileText, label: "Propositions", path: "/app/propositions" },
      { icon: Share2, label: "Reseaux Sociaux", path: "/app/reseaux-sociaux" },
      { icon: Shield, label: "Concurrents SEO", path: "/app/concurrents" },
      { icon: Building2, label: "Agences", path: "/app/agences" },
      { icon: Mail, label: "Suivi Emails", path: "/app/emails" },
      { icon: Inbox, label: "Inbox Projets", path: "/app/inbox-projets" },
      { icon: Settings2, label: "Param. Propositions", path: "/app/parametrages-propositions" },
      { icon: FolderGit2, label: "Control Center", path: "/app/control-center" },
      { icon: CreditCard, label: "Abonnement", path: "/app/abonnement" },
      { icon: BookOpen, label: "Guide", path: "/app/guide" },
    );
    return items;
  } else if (clientType === "agency") {
    // Client Agence: full CRM access (white-label)
    return [
      { icon: LayoutDashboard, label: "Dashboard", path: "/app", exact: true },
      { icon: Users, label: "Mes Clients", path: "/app/clients" },
      { icon: Users, label: "Mes Leads", path: "/app/leads" },
      { icon: FolderKanban, label: "Projets", path: "/app/projets" },
      { icon: FileText, label: "Propositions", path: "/app/propositions" },
      { icon: Receipt, label: "Factures", path: "/app/factures" },
      { icon: LineChart, label: "SEO & Rapports", path: "/app/seo" },
      { icon: Ticket, label: "Tickets", path: "/app/tickets" },
      { icon: MessageSquare, label: "Messagerie", path: "/app/messagerie" },
      { icon: FileBox, label: "Fichiers", path: "/app/fichiers" },
      { icon: ScrollText, label: "Documents", path: "/app/documents" },
      { icon: FileSignature, label: "Contrats", path: "/app/contrats" },
      { icon: Share2, label: "Reseaux Sociaux", path: "/app/reseaux-sociaux" },
      { icon: Shield, label: "Concurrents", path: "/app/concurrents" },
      { icon: Bot, label: "Assistant IA", path: "/app/assistant" },
      { icon: BookOpen, label: "Guide", path: "/app/guide" },
    ];
  } else {
    // Client Standard: limited access (prestations only)
    return [
      { icon: LayoutDashboard, label: "Mon Espace", path: "/app", exact: true },
      { icon: Share2, label: "Mes Reseaux", path: "/app/mes-reseaux" },
      { icon: LineChart, label: "Mon SEO", path: "/app/seo" },
      { icon: Bot, label: "Assistant IA", path: "/app/assistant" },
      { icon: Ticket, label: "Mes Tickets", path: "/app/tickets" },
      { icon: MessageSquare, label: "Messagerie", path: "/app/messagerie" },
      { icon: FileBox, label: "Mes Fichiers", path: "/app/fichiers" },
      { icon: FileSignature, label: "Mes Contrats", path: "/app/contrats" },
      { icon: Receipt, label: "Mes Factures", path: "/app/factures" },
      { icon: User, label: "Mon Profil", path: "/app/profil" },
      { icon: BookOpen, label: "Guide", path: "/app/guide" },
    ];
  }
};

export default function DashboardLayout() {
  const { user, logout, isAdmin, token, activeWorkspace } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const searchRef = useRef(null);
  const API = (process.env.REACT_APP_BACKEND_URL || window.location.origin);

  const isOrgRole = ORG_ROLES.includes(user?.role) && user?.org_id;

  // Check super-admin status
  useEffect(() => {
    if (isAdmin && token) {
      const API = (process.env.REACT_APP_BACKEND_URL || window.location.origin);
      fetch(`${API}/api/super-admin/overview`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => { setIsSuperAdmin(r.ok); })
        .catch(() => setIsSuperAdmin(false));
    }
  }, [isAdmin, token]);

  // Fetch unread message count
  useEffect(() => {
    if (!token) return;
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API}/api/conversations/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
        setUnreadMessages(res.data.unread || 0);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [token, API]);

  // Global search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/api/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(res.data.results || []);
        setSearchOpen(true);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, API]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const typeLabels = { client: "Client", lead: "Lead", project: "Projet", invoice: "Facture" };
  const typeColors = { client: "text-[#0099CC]", lead: "text-[#F59E0B]", project: "text-[#10B981]", invoice: "text-[#8B5CF6]" };

  const navItems = getNavItems(isAdmin, isSuperAdmin, user?.client_type || "standard", user?.role, activeWorkspace);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] dark" data-testid="dashboard-layout">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-[#0B0F19] border-r border-[#2A3441]
        transform transition-transform duration-300 ease-in-out flex flex-col
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-[#2A3441] flex-shrink-0">
          <img src={LOGO_ICON} alt="GLOTECH" className="h-10 w-10 rounded-full object-cover" />
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-[#94A3B8]"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Role / Workspace Badge */}
        <div className="px-4 py-3 border-b border-[#2A3441] flex-shrink-0">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${
            isAdmin 
              ? activeWorkspace === "event_center"
                ? 'bg-[#F59E0B]/20 text-[#F59E0B]'
                : 'bg-[#0099CC]/20 text-[#0099CC]'
              : isOrgRole
                ? 'bg-[#F59E0B]/20 text-[#F59E0B]'
                : 'bg-[#10B981]/20 text-[#10B981]'
          }`}>
            {isAdmin 
              ? activeWorkspace === "event_center" 
                ? <CalendarDays className="h-3 w-3" /> 
                : <Building2 className="h-3 w-3" />
              : isOrgRole ? <CalendarDays className="h-3 w-3" /> : <User className="h-3 w-3" />}
            {isAdmin 
              ? activeWorkspace === "event_center" ? 'EVENT CENTER' : 'GLOTECH WEB' 
              : isOrgRole ? 'EVENT CENTER' : 'ESPACE CLIENT'}
          </div>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1 scrollbar-thin" data-testid="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `
                sidebar-link flex items-center gap-3 px-4 py-2.5 rounded-lg
                transition-colors duration-200 relative text-sm
                ${item.highlight 
                  ? isActive 
                    ? 'bg-amber-500/20 text-amber-400 active border border-amber-500/30'
                    : 'text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 border border-amber-500/20'
                  : isActive 
                    ? 'bg-[#0099CC]/10 text-[#0099CC] active' 
                    : 'text-[#94A3B8] hover:bg-[#151B2B] hover:text-white'
                }
              `}
              data-testid={`sidebar-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              <span className="font-medium">{item.label}</span>
              {item.label === "Messagerie" && unreadMessages > 0 && (
                <span className="ml-auto bg-[#0099CC] text-white text-[10px] font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5" data-testid="unread-badge">
                  {unreadMessages}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Organization / Client Info */}
        {isOrgRole && user?.org && (
          <div className="px-4 pb-2 flex-shrink-0">
            <div className="bg-[#151B2B] rounded-lg p-3 border border-[#F59E0B]/20">
              <p className="text-[#F59E0B] text-xs uppercase tracking-wider mb-1">Mon Organisation</p>
              <p className="text-white font-semibold text-sm truncate">{user.org.org_name}</p>
              <p className="text-[#64748b] text-xs mt-1 capitalize">{ROLE_LABELS[user?.role] || user?.role}</p>
            </div>
          </div>
        )}
        {!isAdmin && !isOrgRole && user?.client && (
          <div className="px-4 pb-2 flex-shrink-0">
            <div className="bg-[#151B2B] rounded-lg p-3 border border-[#2A3441]">
              <p className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1">Mon Entreprise</p>
              <p className="text-white font-semibold text-sm truncate">{user.client.company_name}</p>
              <p className="text-[#64748b] text-xs mt-1">{user.client.sector}</p>
            </div>
          </div>
        )}

        {/* User Info */}
        <div className="p-4 border-t border-[#2A3441] flex-shrink-0">
          <div className="flex items-center gap-3 px-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isAdmin ? 'bg-[#0099CC]/20' : isOrgRole ? 'bg-[#F59E0B]/20' : 'bg-[#10B981]/20'
            }`}>
              <User className={`h-5 w-5 ${isAdmin ? 'text-[#0099CC]' : isOrgRole ? 'text-[#F59E0B]' : 'text-[#10B981]'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">
                {user?.email}
              </p>
              <p className="text-[#64748b] text-xs capitalize">
                {ROLE_LABELS[user?.role] || (isAdmin ? 'Administrateur' : 'Client')}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="h-16 bg-[#0B0F19] border-b border-[#2A3441] flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-[#94A3B8]"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-6 w-6" />
          </Button>

          {/* Title + Workspace Selector + Search */}
          <div className="hidden lg:flex items-center gap-4 flex-1">
            <WorkspaceSelector />
            <h1 className="text-white font-semibold shrink-0">
              {isAdmin 
                ? activeWorkspace === "event_center" ? 'Event Center — Administration' : 'Agency Performance Platform' 
                : isOrgRole ? `${user?.org?.org_name || 'Mon Espace Evenementiel'}` 
                : `Bienvenue, ${user?.client?.company_name || 'Client'}`}
            </h1>
            {isAdmin && (
              <div className="relative ml-8 max-w-md flex-1" ref={searchRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
                    placeholder="Rechercher clients, leads, projets..."
                    className="w-full bg-[#151B2B] border border-[#2A3441] rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder:text-[#64748b] focus:outline-none focus:border-[#0099CC]"
                    data-testid="global-search-input"
                  />
                  {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-[#0099CC]" />}
                </div>
                {searchOpen && searchResults.length > 0 && (
                  <div className="absolute top-full mt-1 w-full bg-[#151B2B] border border-[#2A3441] rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto" data-testid="search-results">
                    {searchResults.map((r) => (
                      <button
                        key={`${r.type}-${r.id}`}
                        onClick={() => { navigate(r.url); setSearchOpen(false); setSearchQuery(""); }}
                        className="w-full text-left px-4 py-3 hover:bg-[#0B0F19] flex items-center gap-3 border-b border-[#2A3441] last:border-0"
                      >
                        <span className={`text-xs font-medium uppercase ${typeColors[r.type] || "text-[#94A3B8]"}`}>{typeLabels[r.type] || r.type}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{r.title}</p>
                          <p className="text-[#64748b] text-xs truncate">{r.subtitle}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-3">
            <div className="lg:hidden">
              <WorkspaceSelector />
            </div>
            <PushNotificationToggle />
            <NotificationBell />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="flex items-center gap-2 text-[#94A3B8] hover:text-white hover:bg-[#151B2B]"
                data-testid="user-menu-btn"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isAdmin ? 'bg-[#0099CC]/20' : isOrgRole ? 'bg-[#F59E0B]/20' : 'bg-[#10B981]/20'
                }`}>
                  <User className={`h-4 w-4 ${isAdmin ? 'text-[#0099CC]' : isOrgRole ? 'text-[#F59E0B]' : 'text-[#10B981]'}`} />
                </div>
                <span className="hidden md:inline text-sm">{user?.email}</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[#151B2B] border-[#2A3441]">
              <div className="px-3 py-2">
                <p className="text-white text-sm font-medium">{user?.email}</p>
                <p className="text-[#64748b] text-xs capitalize">
                  {ROLE_LABELS[user?.role] || (isAdmin ? 'Administrateur' : 'Client')}
                </p>
              </div>
              <DropdownMenuSeparator className="bg-[#2A3441]" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
